import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import {
  ThemaCodesSearchControls,
  ThemaCodesTabs,
} from "components/ThemaCodes";
import { useSession } from "contexts/AuthContext";
import { useDebounce } from "hooks/useDebounce";
import { mapOrganisationLanguageToThema } from "utils/themaLanguageMapping";
import { SearchType, Tab } from "shared/types/thema";

interface BrowseThemaCodesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BrowseThemaCodesDialog({
  open,
  onOpenChange,
}: BrowseThemaCodesDialogProps) {
  const { t } = useTranslation(["books"]);
  const { organisation } = useSession();
  const [query, setQuery] = useState("");
  const [searchType, setSearchType] = useState<SearchType>(SearchType.QUICK);
  const [activeTab, setActiveTab] = useState<Tab>(Tab.SUBJECTS);
  const [showNotesInline, setShowNotesInline] = useState(false);
  const debouncedQuery = useDebounce(query, 300);

  const themaLanguage = organisation.effectiveLanguage
    ? mapOrganisationLanguageToThema(organisation.effectiveLanguage)
    : "en";

  const resetState = () => {
    setQuery("");
    setSearchType(SearchType.QUICK);
    setActiveTab(Tab.SUBJECTS);
    setShowNotesInline(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetState();
    onOpenChange(nextOpen);
  };

  const handleDrillDown = () => {
    setQuery("");
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="min-w-[90vw] max-w-[90vw] h-[85vh] flex flex-col"
        data-testid="browse-thema-codes-dialog"
      >
        <DialogHeader className="items-start">
          <DialogTitle>{t("books:filters.themaCodes.browseTitle")}</DialogTitle>
          <DialogDescription>
            {t("books:filters.themaCodes.browseDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col flex-1 min-h-0">
          <ThemaCodesSearchControls
            searchType={searchType}
            onSearchTypeChange={setSearchType}
            query={query}
            onQueryChange={setQuery}
            showNotesInline={showNotesInline}
            onToggleNotesInline={() => setShowNotesInline((prev) => !prev)}
          />
          <ThemaCodesTabs
            activeTab={activeTab}
            onTabChange={(tab) => setActiveTab(tab)}
            query={debouncedQuery}
            selected={[]}
            themaLanguage={themaLanguage}
            showNotesInline={showNotesInline}
            onDrillDown={handleDrillDown}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
